// src/components/WideSidebar.js

import React, {
  useState,
  useEffect,
  useRef,
  useContext
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './WideSidebar.css';
import { useAuth } from '../context/AuthContext';
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  getDocs,
  writeBatch,
  updateDoc
} from 'firebase/firestore';
import { getAllAssistants } from '../services/openaiService';
import { SearchContext } from '../context/SearchContext';
import { SelectedTeamMemberContext } from '../context/SelectedTeamMemberContext';
import useTeamMembers from '../hooks/useTeamMembers'; // Hook que unifica usuarios reales y/o ficticios
import { db } from '../firebase';


// Función auxiliar para comparar objetos superficialmente
function shallowEqualObj(objA, objB) {
  if (objA === objB) return true;
  const aKeys = Object.keys(objA);
  const bKeys = Object.keys(objB);
  if (aKeys.length !== bKeys.length) return false;
  for (let key of aKeys) {
    if (objA[key] !== objB[key]) return false;
  }
  return true;
}

const WideSidebar = ({ isVisible, crmColumns }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const { setSearchTerm } = useContext(SearchContext);
  const { selectedTeamMember, setSelectedTeamMember } = useContext(SelectedTeamMemberContext);

  // Detectar rutas
  const isThreadsPage   = location.pathname.startsWith('/messages');
  const isChatPage      = location.pathname.startsWith('/chat');
  const isCrmPage       = location.pathname === '/crm';
  const isAnalyticsPage = location.pathname === '/analytics' || location.pathname === '/support';
  const isProfilePage   = location.pathname === '/profile';

  // Detectar /messages/:assistantId
  const messagesMatch = location.pathname.match(/^\/messages\/([^/]+)/);
  const currentAssistantId = messagesMatch ? messagesMatch[1] : null;

  // Estado local para IA
  const [assistantAiEnabled, setAssistantAiEnabled] = useState(true);

  // Detectar si estamos en /bubble/:assistantId
  const bubbleMatch        = location.pathname.match(/^\/bubble\/([^/]+)/);
  const isBubblePage       = !!bubbleMatch;
  const bubbleAssistantId  = isBubblePage ? bubbleMatch[1] : null;

  // Estado local de búsqueda
  const [agentSearchTerm, setAgentSearchTerm] = useState('');
  const [filteredAgents, setFilteredAgents]   = useState([]);
  const [assistants, setAssistants]          = useState([]);
  const [loading, setLoading]                = useState(true);

  // Contador threads en “Mis agentes”
  const [assistantThreadCounts, setAssistantThreadCounts] = useState({});

  // Hook que retorna la lista de miembros (reales + ficticios) con conteo
  const { teamMembersWithCounts } = useTeamMembers(currentUser, isThreadsPage);

  // Estado para almacenar todos los threads unificados
  const [allThreads, setAllThreads] = useState([]); 

  // Respuesta IA global
  const [globalAutoResponseEnabled, setGlobalAutoResponseEnabled] = useState(true);

  // Estado para el switch de Seguimiento Global
  const [globalTrackingEnabled, setGlobalTrackingEnabled] = useState(false);

  // Referencia para desuscribir listeners de assistants
  const unsubscribeAssistantsListenersRef = useRef([]);

  const [assistantLoaded, setAssistantLoaded] = useState({});

  // ------------------------------------------------
  // FUNCIONES PARA ACTIVAR/DESACTIVAR “SEGUIMIENTO”
  // ------------------------------------------------
  const handleToggleGlobalTracking = async () => {
    if (!currentAssistantId) {
      alert('No hay assistantId en la URL');
      return;
    }
    const newValue = !globalTrackingEnabled;
  
    // Mostrar alerta cada vez que se hace toggle
    alert(`Seguimiento global: ${newValue ? 'Encendido' : 'Apagado'}`);
  
    setGlobalTrackingEnabled(newValue);
  
    // Resto de la lógica que ya tenías...
    await updateDoc(doc(db, "assistants", currentAssistantId), {
      globalTrackingEnabled: newValue
    });
  
    try {
      if (newValue) {
        await enableTrackingInAllThreads(currentAssistantId);
        await fetch('https://backend.thethingapp.com/api/trigger-check-tracking', {
          method: 'POST'
        });
      } else {
        await disableTrackingInAllThreads(currentAssistantId);
      }
    } catch (error) {
      console.error('Error al actualizar el seguimiento global:', error);
    }
  };
  
  useEffect(() => {
    if (!currentAssistantId) {
      setGlobalTrackingEnabled(false);
      return;
    }
    const docRef = doc(db, "assistants", currentAssistantId);
    const unsubscribe = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setGlobalTrackingEnabled(data.globalTrackingEnabled ?? false);
      }
    });
  
    return () => unsubscribe();
  }, [currentAssistantId]);
  
  // Desactiva el tracking (trackingActive = false) en todos los threads
  async function disableTrackingInAllThreads(assistantId) {
    const threadsRef = collection(db, `threads_${assistantId}`);
    const snapshot   = await getDocs(threadsRef);
    const batch      = writeBatch(db);

    snapshot.forEach((docSnap) => {
      batch.update(docSnap.ref, { trackingActive: false });
    });

    await batch.commit();
    console.log(`Se desactivó el seguimiento en todos los threads de ${assistantId}`);
  }

  // Activa el tracking (trackingActive = true) en todos los threads
  async function enableTrackingInAllThreads(assistantId) {
    const threadsRef = collection(db, `threads_${assistantId}`);
    const snapshot   = await getDocs(threadsRef);
    const batch      = writeBatch(db);

    snapshot.forEach((docSnap) => {
      batch.update(docSnap.ref, { trackingActive: true });
    });

    await batch.commit();
    console.log(`Se activó el seguimiento en todos los threads de ${assistantId}`);
  }

  // ----------------------------------------------
  // EFECTO: Toggle assistant AI en “assistants”
  // ----------------------------------------------
  useEffect(() => {
    if (!currentAssistantId) {
      setAssistantAiEnabled(true);
      return;
    }
    const docRef = doc(db, 'assistants', currentAssistantId);
    const unsubscribe = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setAssistantAiEnabled(data.autoResponseEnabled ?? true);
      } else {
        setAssistantAiEnabled(true);
      }
    });
    return () => unsubscribe();
  }, [currentAssistantId]);

  // Funciones para des/habilitar la IA en TODOS los threads de un assistant
  async function disableAiInAllThreads(assistantId) {
    const threadsRef = collection(db, `threads_${assistantId}`);
    const snapshot   = await getDocs(threadsRef);
    const batch      = writeBatch(db);

    snapshot.forEach((docSnap) => {
      batch.update(docSnap.ref, { autoResponseEnabled: false });
    });
    await batch.commit();
  }

  async function enableAiInAllThreads(assistantId) {
    const threadsRef = collection(db, `threads_${assistantId}`);
    const snapshot   = await getDocs(threadsRef);
    const batch      = writeBatch(db);

    snapshot.forEach((docSnap) => {
      batch.update(docSnap.ref, { autoResponseEnabled: true });
    });
    await batch.commit();
  }

  const handleToggleAssistantAi = async () => {
    if (!currentAssistantId) {
      alert('No hay assistantId en la URL');
      return;
    }
    const newValue = !assistantAiEnabled;
    setAssistantAiEnabled(newValue);

    // (1) Actualizamos en “assistants”
    await updateDoc(doc(db, 'assistants', currentAssistantId), {
      autoResponseEnabled: newValue,
    });

    // (2) Apagar/encender en todos los threads
    if (!newValue) {
      await disableAiInAllThreads(currentAssistantId);
    } else {
      await enableAiInAllThreads(currentAssistantId);
    }
  };

  // =========================================================
  // (1) Leer preferencia global IA en la colección “users”
  // =========================================================
  useEffect(() => {
    if (!currentUser) return;
    const userDocRef = doc(db, 'users', currentUser.uid);

    const unsubscribe = onSnapshot(userDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (typeof data.globalAutoResponseEnabled === 'boolean') {
          setGlobalAutoResponseEnabled(data.globalAutoResponseEnabled);
        } else {
          setGlobalAutoResponseEnabled(true);
        }
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleToggleGlobalAutoResponse = async () => {
    const newValue = !globalAutoResponseEnabled;
    setGlobalAutoResponseEnabled(newValue);
    if (!currentUser) return;

    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        globalAutoResponseEnabled: newValue,
      });
    } catch (error) {
      console.error('Error al actualizar globalAutoResponseEnabled:', error);
    }
  };

  // =========================================================
  // (2) Obtener la lista de asistentes del usuario (Hook)
  // =========================================================
  useEffect(() => {
    const fetchAssistants = async () => {
      if (!currentUser) return;
      setLoading(true);
      try {
        const assistantsData = await getAllAssistants(currentUser.uid);
        setAssistants(assistantsData);
      } catch (error) {
        console.error('Error al obtener asistentes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAssistants();
  }, [currentUser]);

  // Búsqueda local de “Mis agentes”
  useEffect(() => {
    if (agentSearchTerm.trim() === '') {
      setFilteredAgents(assistants);
    } else {
      const term = agentSearchTerm.toLowerCase();
      setFilteredAgents(
        assistants.filter((agent) =>
          agent.name.toLowerCase().includes(term)
        )
      );
    }
  }, [assistants, agentSearchTerm]);

  // =================================================================
  // (3) SUSCRIPCIÓN a TODOS los threads de TODOS los “assistants”
  // =================================================================
  useEffect(() => {
    if (!isThreadsPage || !assistants.length) return;
  
    // Limpiar listeners anteriores
    unsubscribeAssistantsListenersRef.current.forEach((unsub) => unsub());
    unsubscribeAssistantsListenersRef.current = [];
  
    let aggregatedThreads = [];
  
    assistants.forEach((assistant) => {
      const ref = collection(db, `threads_${assistant.id}`);
      const unsub = onSnapshot(
        ref,
        (snapshot) => {
          // Marcar como "cargado" el snapshot de este asistente
          setAssistantLoaded((prev) => ({
            ...prev,
            [assistant.id]: true, // <-- true al recibir primer snapshot
          }));
  
          // Obtener threads de este asistente
          const assistantThreads = snapshot.docs.map((doc) => ({
            threadId: doc.id,
            ...doc.data(),
            assistantId: assistant.id,
          }));
  
          // Quitar threads previos de este assistant
          aggregatedThreads = aggregatedThreads.filter(
            (t) => t.assistantId !== assistant.id
          );
  
          // Agregarlos de nuevo
          aggregatedThreads = [...aggregatedThreads, ...assistantThreads];
  
          // Actualizar todos los threads
          setAllThreads([...aggregatedThreads]);
        },
        (error) => {
          console.error(`Error en snapshot threads_${assistant.id}:`, error);
        }
      );
      unsubscribeAssistantsListenersRef.current.push(unsub);
    });
  
    return () => {
      unsubscribeAssistantsListenersRef.current.forEach((unsub) => unsub());
      unsubscribeAssistantsListenersRef.current = [];
    };
  }, [assistants, isThreadsPage]);

  // =============================================
  //  Conteo de threads por assistantId
  // =============================================
  useEffect(() => {
    if (!isThreadsPage) return;
  
    // Crear objeto con la cuenta para cada assistant
    const newCounts = {};
    assistants.forEach((assistant) => {
      newCounts[assistant.id] = 0; // <-- Inicialmente 0
    });
  
    allThreads.forEach((t) => {
      // Sumar 1 para el assistantId correspondiente
      newCounts[t.assistantId] += 1;
    });
  
    if (!shallowEqualObj(newCounts, assistantThreadCounts)) {
      setAssistantThreadCounts(newCounts);
    }
  }, [isThreadsPage, assistants, allThreads, assistantThreadCounts]);
  
  

  // =================================
  // FUNCIONES DE NAVEGACIÓN
  // =================================
  const handleAgentSearchTermChange = (term) => {
    setAgentSearchTerm(term);
  };

  const handleSelectAgent = (agent) => {
    setSelectedTeamMember(null);
    navigate(`/messages/${agent.id}`);
  };

  const handleSelectTeamMemberClick = (member) => {
    setSelectedTeamMember(member);
    // Ejemplo de ruta => "/messages/team-{member.id}"
    navigate(`/messages/team-${member.id}`);
  };

  

  // =========================
  // Render principal
  // =========================
  return (
    <div className={`wide-sidebar ${isVisible ? 'visible' : 'hidden'}`}>
      {isBubblePage ? (
        // (1) BUBBLE PAGE
        <div className="wide-sidebar-content">
          <h2>Prueba tu agente</h2>
          <ul className="scrollable-list">
            <li
              className="list-item"
              onClick={() => navigate(`/chat/${bubbleAssistantId}`)}
            >
              Volver al entrenamiento
            </li>
          </ul>
        </div>
      ) : (
        // (2) OTRAS RUTAS
        <>
          {isThreadsPage ? (
            // (2A) /messages
            <div className="wide-sidebar-content">
              <h2 className="titulos-threads">Mis agentes</h2>

              <div className="search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Buscar"
                  value={agentSearchTerm}
                  onChange={(e) => handleAgentSearchTermChange(e.target.value)}
                />
              </div>

              {/* Lista de AGENTES (WhatsApp) */}
              <ul className="scrollable-list">
                {loading ? (
                  <li className="list-item">Cargando...</li>
                ) : filteredAgents.length > 0 ? (
                  filteredAgents.map((agent) => (
<li
  key={agent.id}
  className="list-item"
  onClick={() => handleSelectAgent(agent)}
>
  <div className="agent-info">
    <p className="agent-name">{agent.name}</p>
    <span className="thread-count">
      {assistantLoaded[agent.id]
        // Si ya cargó la suscripción => mostramos su número (sea 0 o más)
        ? assistantThreadCounts[agent.id] ?? 0
        // Si NO ha cargado => spinner
        : <div className="custom-spinner" />
      }
    </span>
  </div>
</li>

                  ))
                ) : (
                  <li className="list-item">No se encontraron agentes</li>
                )}

                {/* Lista de TEAM MEMBERS (reales o ficticios unificados) */}
                {teamMembersWithCounts.length > 0 &&
                  teamMembersWithCounts.map((member) => (
                    <li
                      key={`member-${member.id}`}
                      className="list-item"
                      onClick={() => handleSelectTeamMemberClick(member)}
                    >
                      <div className="agent-info">
                        <p className="agent-name">
                          {member.nombre} {member.apellido}
                        </p>
                        <span className="thread-count">
                          {member.conversationCount || 0}
                        </span>
                      </div>
                    </li>
                  ))}
              </ul>

              {/* Switch para IA del assistant actual */}
              {currentAssistantId && (
                <div className="global-autoresponse-switch-container">
                  <span className="global-autoresponse-label">Respuesta IA Global</span>
                  <label className="switch-ia-global">
                    <input
                      type="checkbox"
                      checked={assistantAiEnabled}
                      onChange={handleToggleAssistantAi}
                    />
                    <span className="slider pending-slider"></span>
                  </label>
                </div>
              )}

              {/* Switch para seguimiento global */}
              {currentAssistantId && (
  <div className="global-tracking-button-container">
    <button
      className={`global-tracking-button ${globalTrackingEnabled ? 'on' : 'off'}`}
      onClick={handleToggleGlobalTracking}
    >
      {globalTrackingEnabled ? 'Seguimiento global' : 'Seguimiento global'}
    </button>
  </div>
)}

            </div>
          
          ) : isChatPage ? (
            // (2B) /chat
            <div className="wide-sidebar-content">
              <h2>Entrenador <br /> de Agentes</h2>
              <ul className="scrollable-list">
                {loading ? (
                  <li className="list-item">Cargando...</li>
                ) : assistants.length > 0 ? (
                  assistants.map((assistant) => (
                    <li
                      key={assistant.id}
                      className="list-item"
                      onClick={() => navigate(`/chat/${assistant.id}`)}
                    >
                      {assistant.name}
                    </li>
                  ))
                ) : (
                  <li className="list-item">No hay agentes disponibles</li>
                )}
              </ul>
            </div>
          
          ) : isCrmPage ? (
            // (2C) /crm
            <div className="wide-sidebar-content">
              <h2>Panel de <br />seguimiento</h2>
              <div className="search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Buscar por nombre o teléfono"
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          
          ) : isAnalyticsPage ? (
            // (2D) /analytics o /support
            <div className="wide-sidebar-content">
              <h2>Próximamente</h2>
            </div>
          
          ) : isProfilePage ? (
            // (2E) /profile
            <div className="wide-sidebar-content">
              <h2>Perfil</h2>
              <ul className="scrollable-list">
                <li
                  className="list-item"
                  onClick={() => {
                    document
                      .getElementById('conversations-counter')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Conversaciones
                </li>
                <li
                  className="list-item"
                  onClick={() => {
                    document
                      .getElementById('subscription-counter')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Suscripción
                </li>
                <li
                  className="list-item"
                  onClick={() => {
                    document
                      .getElementById('add-member-button')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Equipo
                </li>
              </ul>
            </div>
          
          ) : (
            // (2F) Ruta default
            <div className="wide-sidebar-content">
              <h2>Comenzar <br /> The Thing</h2>
              <ul className="scrollable-list">
                <li className="list-item"
                onClick={() => {
                  document
                    .getElementById('misagentes')
                    ?.scrollIntoView({ behavior: 'smooth' });
                }}
                >Mis Agentes</li>
                <li className="list-item"
                onClick={() => {
                  document
                    .getElementById('basesdedatos')
                    ?.scrollIntoView({ behavior: 'smooth' });
                }}
                >Mis Bases de Datos</li>
                <li className="list-item"
                onClick={() => {
                  document
                    .getElementById('tutorial-section')
                    ?.scrollIntoView({ behavior: 'smooth' });
                }}
                >Tutoriales</li>
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WideSidebar;
